<script lang="ts" setup>
import { Roles } from '@/constants'
import type {
  FeedbacksAdminModel,
  ReportsModel,
  TestMetaResponse,
} from '@/services/http/httpResponses'
import testsHttpService from '@/services/http/testsHttpService'
import type {
  Id,
  professionalAreas,
  professionalValues,
  TestResultDiagramModel,
} from '@raildev/shared-utils'
import type { TabPassThroughMethodOptions } from 'primevue/tab'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getPaidStatus } from '@/helpers/statusHelpers'
import { isNullOrEmpty } from '@/helpers/stringFunctions'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import jwtService from '@/services/jwtService'
import PromptsEdit from '@/components/PromptsEdit.vue'
import TestResults from '@/components/TestResults.vue'
import ReadOnlyPrompts from '@/components/ReadOnlyPrompts.vue'
import { useToast } from 'primevue/usetoast'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.CareerGuidanceSpecialist],
  },
})

const tablistClasses = {
  activeBar: { class: 'bg-blue-500' },
} as const

const tabClasses = {
  root: ({ context }: TabPassThroughMethodOptions) =>
    context.active ? 'text-blue-500' : 'text-grey-500',
} as const

const router = useRouter()

let role: Roles | null = null
const token = jwtService.getToken()
if (token) {
  role = jwtDecode<JwtPayload & { role: Roles }>(token).role
}

const testId = (
  router.currentRoute.value.name === '/test-analytics/[id]'
    ? router.currentRoute.value.params.id
    : undefined
) as Id

const {
  billStatus,
  results: testResultsRaw,
  personalInfo: testPersonalInfo,
  webDiagram,
  histogram,
} = await testsHttpService.getTestResults(testId)

const testResults = ref(testResultsRaw.sort((a, b) => b.index - a.index))

const { messageHistories: prompts } = await testsHttpService.getPrompts(testId)

const reports = ref<ReportsModel[]>([])
const reportsResponse = await testsHttpService.getReports(testId)
reports.value = reportsResponse.reports

const { questions } = await testsHttpService.getQuestions(testId)
questions.sort((a, b) => a.index - b.index)
questions.forEach((question) => question.options.sort((a, b) => a.index - b.index))

const feedbacksResponse = await testsHttpService.getFeedbacks(testId)

const feedbacks = ref<FeedbacksAdminModel[]>(feedbacksResponse.feedbacks)
const formattedFeedbacks = computed(() => {
  return feedbacks.value.map((feedback) => ({
    ...feedback,
    comment: isNullOrEmpty(feedback.comment) ? '-' : feedback.comment,
    email: feedback.email ?? '-',
    phoneNumber: feedback.phoneNumber !== null ? `+${feedback.phoneNumber}` : '-',
    createdAt: feedback.createdAt.toLocaleDateString('ru-RU'),
  }))
})

const { user: personalInformation } = await testsHttpService.getPersonalInformation(testId)

const testMeta = ref<TestMetaResponse | null>(null)
if (role === Roles.Admin) {
  testMeta.value = await testsHttpService.getTestMeta(testId)
}

const professionalAreas = computed(() => {
  const data = webDiagram !== null ? Object.entries(webDiagram) : []
  return data as [professionalAreas, TestResultDiagramModel][]
})
const professionalValues = computed(() => {
  const data = histogram !== null ? Object.entries(histogram) : []
  return data as [professionalValues, TestResultDiagramModel][]
})

const radarDiagramData = ref<ReturnType<typeof setRadarDiagramData>>()
const radarDiagramOptions = ref<ReturnType<typeof setRadarDiagramOptions>>()
const horizontalBarDiagramData = ref<ReturnType<typeof setHorizontalBarDiagramData>>()
const horizontalBarDiagramOptions = ref<ReturnType<typeof setHorizontalBarDiagramOptions>>()

const updateDiagrams = () => {
  radarDiagramData.value = setRadarDiagramData()
  radarDiagramOptions.value = setRadarDiagramOptions()
  horizontalBarDiagramData.value = setHorizontalBarDiagramData()
  horizontalBarDiagramOptions.value = setHorizontalBarDiagramOptions()
}

const setRadarDiagramData = () => {
  const documentStyle = getComputedStyle(document.documentElement)
  const data: number[] = new Array(6).fill(0).map((_, index) => {
    const testDiagramData = professionalAreas.value[index][1]
    return testDiagramData.score
  })

  return {
    labels: [
      'Гуманитарное',
      'Техническое',
      'Естественное',
      'Экономическое',
      'Социальное',
      'Творческое',
    ],
    datasets: [
      {
        borderColor: documentStyle.getPropertyValue('--p-blue-400'),
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        pointBackgroundColor: documentStyle.getPropertyValue('--p-blue-400'),
        pointBorderColor: documentStyle.getPropertyValue('--p-blue-400'),
        data,
      },
    ],
  }
}
const setRadarDiagramOptions = () => {
  const documentStyle = getComputedStyle(document.documentElement)
  return {
    aspectRatio: 5 / 3.5,
    animations: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    layout: {
      padding: 0,
    },
    scales: {
      r: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 2,
          font: {
            size: 16,
          },
        },
        pointLabels: {
          padding: 0,
          color: documentStyle.getPropertyValue('--p-text-color'),
          font: {
            size: 16,
          },
        },
      },
    },
  }
}

const setHorizontalBarDiagramData = () => {
  const documentStyle = getComputedStyle(document.documentElement)
  const data: number[] = new Array(7).fill(0).map((_, index) => {
    const testDiagramData = professionalValues.value[index][1]
    return testDiagramData.score
  })

  return {
    labels: [
      'Стабильность',
      'Благосостояние',
      'Развитие',
      'Баланс работа/жизнь',
      'Социальная значимость',
      'Инновации',
      'Творчество',
    ],
    datasets: [
      {
        data,
        backgroundColor: [
          documentStyle.getPropertyValue('--p-red-500'),
          documentStyle.getPropertyValue('--p-lime-500'),
          documentStyle.getPropertyValue('--p-orange-500'),
          documentStyle.getPropertyValue('--p-fuchsia-500'),
          documentStyle.getPropertyValue('--p-cyan-500'),
          documentStyle.getPropertyValue('--p-green-500'),
          documentStyle.getPropertyValue('--p-yellow-500'),
        ],
        barThickness: 16,
      },
    ],
  }
}
const setHorizontalBarDiagramOptions = () => {
  return {
    aspectRatio: 2 / 1.25,
    indexAxis: 'y',
    animations: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    layout: {
      padding: 0,
    },
    scales: {
      x: {
        max: 10,
        title: {
          display: true,
          text: 'Уровень важности',
          font: {
            size: 16,
          },
        },
        ticks: {
          font: {
            size: 16,
          },
        },
      },
      y: {
        ticks: {
          font: {
            size: 16,
          },
        },
      },
    },
  }
}

const promptEditDialogOpen = ref(false)
const reportDialogOpen = ref(false)

const selectedReportId = ref<Id | null>(null)

const updateReportsList = async (testId: Id) => {
  const response = await testsHttpService.getReports(testId)
  reports.value = response.reports
}

const openPromptEditDialog = () => {
  promptEditDialogOpen.value = true
}

const openReportDialog = (reportId: Id) => {
  selectedReportId.value = reportId
  reportDialogOpen.value = true
}

const toast = useToast()

const activateReport = async (reportId: Id) => {
  const response = await testsHttpService.activateReport(reportId)
  await updateReportsList(testId)

  const newTestResults = await testsHttpService.getTestResults(testId)
  testResults.value = newTestResults.results.sort((a, b) => b.index - a.index)

  if (response.status === 200) {
    toast.add({
      severity: 'success',
      detail: 'Отчёт успешно активирован',
      life: 2500,
    })
  }
}

const testStatusLabels: Record<number, string> = {
  0: 'None',
  1: 'Создан',
  2: 'Генерация вопросов',
  3: 'Начат',
  4: 'Генерация результатов',
  5: 'Завершён',
}

onMounted(() => {
  if (webDiagram !== null && histogram !== null) {
    updateDiagrams()
  }
})
</script>

<template>
  <Dialog
    v-model:visible="reportDialogOpen"
    :style="{ width: '90vw' }"
    header="Отчёт"
    :closeOnEscape="true"
    :modal="true"
  >
    <Tabs value="test-results">
      <TabList :pt="tablistClasses">
        <Tab value="test-results" :pt="tabClasses">Результаты теста</Tab>
        <Tab value="prompts" :pt="tabClasses">Использованные промпты</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="test-results">
          <TestResults v-if="selectedReportId" :reportId="selectedReportId" />
        </TabPanel>
        <TabPanel value="prompts">
          <ReadOnlyPrompts v-if="selectedReportId" :reportId="selectedReportId" />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </Dialog>

  <Dialog
    v-model:visible="promptEditDialogOpen"
    :style="{ width: '90vw' }"
    header="Промпты"
    :after-hide="updateReportsList(testId)"
    :closeOnEscape="true"
    :modal="true"
  >
    <PromptsEdit :testId="testId" :personalData="personalInformation" />
  </Dialog>

  <Tabs :value="role === 'Admin' ? 'meta' : 'personal-information'">
    <TabList :pt="tablistClasses">
      <Tab v-if="role === 'Admin'" value="meta" :pt="tabClasses">Мета</Tab>
      <Tab value="personal-information" :pt="tabClasses">Персональная информация</Tab>
      <Tab value="questions" :pt="tabClasses">Вопросы и ответы</Tab>
      <Tab value="results" :pt="tabClasses">Результаты теста</Tab>
      <Tab v-if="role === 'Admin'" value="feedbacks" :pt="tabClasses">Отзывы</Tab>
      <Tab v-if="role === 'Admin'" value="prompts" :pt="tabClasses">Промпты</Tab>
      <Tab v-if="role === 'Admin'" value="sandbox" :pt="tabClasses">Песочница</Tab>
    </TabList>
    <TabPanels>
      <TabPanel value="personal-information">
        <template v-if="testMeta">
          <section class="mb-4 md:mb-6">
            <h3 class="text-lg font-semibold mb-3">Контакты и профиль</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <p>
                <span class="font-medium text-gray-500">Email: </span
                >{{ testMeta.userEmail ?? '—' }}
              </p>
              <p>
                <span class="font-medium text-gray-500">Телефон: </span
                >{{ testMeta.userPhone ? `+${testMeta.userPhone}` : '—' }}
              </p>
              <p>
                <span class="font-medium text-gray-500">Возраст: </span>{{ testMeta.age ?? '—' }}
              </p>
              <p>
                <span class="font-medium text-gray-500">Требуемое образование:</span
                >{{ testMeta.education ?? '—' }}
              </p>
              <p>
                <span class="font-medium text-gray-500">Тип населённого пункта: </span
                >{{ testMeta.livingPlaceType ?? '—' }}
              </p>
              <p>
                <span class="font-medium text-gray-500">Населённый пункт: </span
                >{{ testMeta.specificLivingPlace ?? '—' }}
              </p>
              <p v-if="testMeta.additionalInformation" class="sm:col-span-2">
                <span class="font-medium text-gray-500">Дополнительная информация: </span
                >{{ testMeta.additionalInformation }}
              </p>
            </div>
          </section>
        </template>

        <section class="mb-4 md:mb-6">
          <h3 class="text-lg font-semibold mb-2">Текущая активность</h3>
          <p class="text-sm">
            {{ personalInformation.currentActivity?.name ?? 'Неизвестно' }}
          </p>
        </section>

        <section class="mb-4 md:mb-6">
          <h3 class="text-lg font-semibold mb-2">Интересы</h3>
          <div class="flex gap-3 flex-wrap">
            <Chip
              v-for="interest in personalInformation.interests"
              :key="interest.id"
              class="bg-blue-200 text-sm"
              :label="interest.name"
            />
          </div>
        </section>

        <section class="mb-4 md:mb-6">
          <h3 class="text-lg font-semibold mb-2">Хобби</h3>
          <div class="flex gap-3 flex-wrap">
            <Chip
              v-for="hobby in personalInformation.hobbies"
              :key="hobby.id"
              class="bg-blue-200 text-sm"
              :label="hobby.name"
            />
          </div>
        </section>

        <section class="mb-4 md:mb-6">
          <h3 class="text-lg font-semibold mb-2">Дисциплины</h3>
          <div class="flex gap-3 flex-wrap">
            <Chip
              v-for="subject in personalInformation.subjects"
              :key="subject.id"
              class="bg-blue-200 text-sm"
              :label="subject.name"
            />
          </div>
        </section>

        <section class="mb-4 md:mb-6">
          <h3 class="text-lg font-semibold mb-2">Личностные характеристики</h3>
          <div class="flex gap-3 flex-wrap">
            <Chip
              v-for="characteristic in personalInformation.personalCharacteristics"
              :key="characteristic.id"
              class="bg-blue-200 text-sm"
              :label="characteristic.name"
            />
          </div>
        </section>
      </TabPanel>
      <TabPanel value="questions">
        <div
          v-for="question in questions"
          :key="question.index"
          class="not-first:pt-4 pb-4 not-last:border-b-1 border-stone-200"
        >
          <h2 class="text-lg font-medium">Вопрос №{{ question.index }}</h2>
          <p>{{ question.text }}</p>

          <template v-if="question.otherOption === null">
            <h3 class="mt-2 text-lg font-medium">Варианты ответа:</h3>
            <div class="flex flex-col gap-0.5">
              <p
                v-for="{ option, index, isSelected, text } in question.options"
                :key="index"
                :class="{ 'text-green-600 font-medium': isSelected }"
              >
                {{ `${option}` }}. {{ text }}
              </p>
            </div>
          </template>
          <template v-else>
            <h3 class="mt-2 text-lg font-medium">Ответ пользователя:</h3>
            <p>{{ question.otherOption }}</p>
          </template>
        </div>
      </TabPanel>
      <TabPanel value="results">
        <template v-if="testResults.length > 0">
          <div class="mx-auto flex flex-col gap-1 items-center">
            <p
              :class="
                billStatus === 5 ? 'border-green-600 text-green-600' : 'border-red-600 text-red-600'
              "
              class="mt-4 inline-flex p-4 border-2 text-xl rounded-xl font-medium uppercase self-center"
            >
              {{ getPaidStatus(billStatus) }}
            </p>

            <section class="w-full py-4 flex gap-4 lg:gap-6 items-center flex-col xl:flex-row">
              <img
                width="400"
                height="400"
                class="max-w-90 lg:max-w-100 w-full h-auto"
                src="../../assets/education.webp"
              />
              <Fieldset
                legend="Ключевые черты личности и психотип"
                class="border-[#7c98ff] rounded-4xl"
                :pt="{
                  legend: 'text-xl text-stone-950 text-center',
                  root: 'px-4 pb-4 sm:px-6 sm:pb-6',
                }"
              >
                <p class="text-base sm:text-lg">
                  {{ testPersonalInfo?.personality }}
                </p>
              </Fieldset>
            </section>

            <section class="w-full py-4 flex gap-4 lg:gap-6 items-center flex-col xl:flex-row">
              <Fieldset
                legend="Что о тебе говорят твои интересы и хобби"
                class="border-[#7c98ff] rounded-4xl"
                :pt="{ legend: 'text-xl text-stone-950 text-center', root: 'px-6 pb-6' }"
              >
                <p class="text-base sm:text-lg">
                  {{ testPersonalInfo?.interests }}
                </p>
              </Fieldset>
              <img
                width="400"
                height="360"
                class="order-first xl:order-1 max-w-90 lg:max-w-100 w-full h-auto"
                src="../../assets/hobbies.webp"
              />
            </section>

            <section class="w-full py-4 flex gap-4 lg:gap-6 items-center flex-col xl:flex-row">
              <Chart
                type="radar"
                :data="radarDiagramData"
                :options="radarDiagramOptions"
                class="w-full max-w-[500px] lg:max-w-[600px] xl:max-w-[500px]"
              />
              <Fieldset
                legend="Склонность к различным профессиональным направлениям"
                class="border-[#7c98ff] rounded-4xl"
                :pt="{ legend: 'text-xl text-stone-950 text-center', root: 'px-6 pb-6' }"
              >
                <p
                  v-for="[key, value] in professionalAreas"
                  :key="key"
                  class="text-base sm:text-lg"
                >
                  {{ value.explanation }}
                </p>
              </Fieldset>
            </section>

            <section class="w-full py-4 flex gap-4 lg:gap-6 items-center flex-col xl:flex-row">
              <Fieldset
                legend="Профессиональные ценности и предпочтения"
                class="border-[#7c98ff] rounded-4xl"
                :pt="{ legend: 'text-xl text-stone-950 text-center', root: 'px-6 pb-6' }"
              >
                <p
                  v-for="[key, value] in professionalValues"
                  :key="key"
                  class="text-base sm:text-lg"
                >
                  {{ value.explanation }}
                </p>
              </Fieldset>
              <Chart
                type="bar"
                :data="horizontalBarDiagramData"
                :options="horizontalBarDiagramOptions"
                class="order-first xl:order-1 w-full max-w-[500px] lg:max-w-[600px] xl:max-w-[500px]"
              />
            </section>

            <section class="flex flex-col py-4 w-full">
              <h2 class="text-xl sm:text-2xl text-center font-bold">
                Наша рекомендация ТОП-5 профессий, которые подойдут тебе:
              </h2>
              <div class="mt-4 sm:mt-6 flex flex-col gap-4 sm:gap-6">
                <div
                  v-for="profession in testResults"
                  :key="profession.id"
                  class="border border-[#7c98ff] rounded-4xl p-4 sm:p-8"
                >
                  <h3 class="flex items-center gap-1 font-bold text-xl sm:text-2xl lg:text-3xl">
                    <span>{{ profession.index }}. {{ profession.professionName }}</span>
                  </h3>
                  <div class="mt-2 sm:mt-4">
                    <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">Описание профессии:</h4>
                    <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                      {{ profession.description }}
                    </p>
                  </div>
                  <div class="mt-2 sm:mt-4">
                    <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">
                      Почему мы считаем что она тебе подходит:
                    </h4>
                    <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                      {{ profession.reason }}
                    </p>
                  </div>
                  <div class="mt-2 sm:mt-4">
                    <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">
                      Спрос и перспектива:
                    </h4>
                    <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                      {{ profession.demand }}
                    </p>
                  </div>

                  <div class="flex flex-col gap-2 sm:gap-4 mt-2 sm:mt-4">
                    <div class="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <div class="flex-1">
                        <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">
                          Рекомендуемая специальность:
                        </h4>
                        <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                          {{ profession.recommendedMajor }}
                        </p>
                      </div>

                      <div class="flex-1">
                        <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">Специализация:</h4>
                        <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                          {{ profession.specialization }}
                        </p>
                      </div>
                    </div>
                    <div class="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <div class="flex-1">
                        <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">
                          Профильные предметы:
                        </h4>
                        <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                          {{ profession.majorSubject }}
                        </p>
                      </div>

                      <div class="flex-1">
                        <h4 class="font-bold text-[#7c98ff] text-lg sm:text-xl">
                          Код специальности:
                        </h4>
                        <p class="mt-1 sm:mt-2 text-base sm:text-lg">
                          {{ profession.professionCode }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </template>
        <template v-else>
          <Message
            class="mx-auto max-w-xl"
            size="large"
            severity="info"
            icon="pi pi-exclamation-circle"
          >
            Данные о результатах теста не найдены
          </Message>
        </template>
      </TabPanel>
      <TabPanel v-if="role === 'Admin'" value="feedbacks">
        <template v-if="formattedFeedbacks.length > 0">
          <div class="flex gap-4 flex-wrap">
            <Panel
              v-for="(feedback, index) in formattedFeedbacks"
              :key="feedback.id"
              class="basis-[calc(50%-16px)]"
            >
              <template #header>
                <h3 class="text-lg font-semibold text-gray-800">Отзыв №{{ index + 1 }}</h3>
              </template>
              <p><span class="font-medium">Имя пользователя: </span>{{ feedback.userName }}</p>
              <p><span class="font-medium">Телефон: </span>{{ feedback.phoneNumber }}</p>
              <p><span class="font-medium">Почта: </span>{{ feedback.email }}</p>
              <p><span class="font-medium">Дата создания: </span>{{ feedback.createdAt }}</p>
              <p><span class="font-medium">Рейтинг: </span>{{ feedback.rating }}</p>
              <p><span class="font-medium">Сообщение: </span>{{ feedback.comment }}</p>
            </Panel>
          </div>
        </template>
        <template v-else>
          <Message
            class="mx-auto max-w-xl"
            size="large"
            severity="info"
            icon="pi pi-exclamation-circle"
          >
            Данные об отзывах не найдены
          </Message>
        </template>
      </TabPanel>
      <TabPanel v-if="role === 'Admin'" value="prompts">
        <template v-if="prompts.length > 0">
          <div
            v-for="prompt in prompts"
            :key="prompt.index"
            class="not-first:pt-4 pb-4 not-last:border-b-1 border-stone-200"
          >
            <h3 class="text-xl mb-4 text-center">
              Роль: <span class="font-medium">{{ prompt.messageRole }}</span>
            </h3>
            <pre class="whitespace-pre-wrap break-words overflow-x-auto font-[inherit]">
               {{ prompt.message }}
            </pre>
          </div>
        </template>

        <template v-else>
          <Message
            class="mx-auto max-w-xl"
            size="large"
            severity="info"
            icon="pi pi-exclamation-circle"
          >
            Данные о промптах не найдены
          </Message>
        </template>
      </TabPanel>
      <TabPanel value="sandbox">
        <DataTable ref="dt" :value="reports" v-if="reports.length > 0" dataKey="id">
          <Column header="Версия" style="min-width: 12rem">
            <template #body="slotProps">
              {{ slotProps.data.version }}
            </template>
          </Column>
          <Column header="Дата создания" style="min-width: 12rem">
            <template #body="slotProps">
              {{ new Date(slotProps.data.createdAt).toLocaleDateString('RU-ru') }}
              {{ new Date(slotProps.data.createdAt).toLocaleTimeString('RU-ru') }}
            </template>
          </Column>
          <Column header="Статус" style="min-width: 12rem">
            <template #body="slotProps">
              {{ slotProps.data.isActive ? 'Активен' : 'Неактивен' }}
            </template>
          </Column>
          <Column header="Аналитика" style="min-width: 12rem">
            <template #body="slotProps">
              <Button severity="info" @click="openReportDialog(slotProps.data.id)">
                Открыть отчёт
              </Button>
            </template>
          </Column>
          <Column header="Действия" style="min-width: 12rem">
            <template #body="slotProps">
              <Button v-if="!slotProps.data.isActive" @click="activateReport(slotProps.data.id)">
                Активировать
              </Button>
            </template>
          </Column>
        </DataTable>
        <div v-else>
          <Message
            class="mx-auto max-w-xl"
            size="large"
            severity="info"
            icon="pi pi-exclamation-circle"
          >
            Данные о перегенерированных отчётах не найдены
          </Message>
        </div>

        <Button class="flex mx-auto mt-5" label="Перегенерировать" @click="openPromptEditDialog" />
      </TabPanel>
      <TabPanel v-if="role === 'Admin'" value="meta">
        <template v-if="testMeta">
          <div class="flex flex-col gap-6">
            <section>
              <h3 class="text-lg font-semibold mb-3">Тест</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <p>
                  <span class="font-medium text-gray-500">Короткий ID: </span>{{ testMeta.shortId }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Статус: </span
                  >{{ testStatusLabels[testMeta.status] ?? testMeta.status }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Начат: </span
                  >{{ new Date(testMeta.startDateTime).toLocaleString('ru-RU') }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Завершён: </span
                  >{{
                    testMeta.endDateTime
                      ? new Date(testMeta.endDateTime).toLocaleString('ru-RU')
                      : '—'
                  }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Вопросов пройдено: </span
                  >{{ testMeta.answeredQuestions }} из {{ testMeta.totalQuestions }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Язык теста: </span
                  >{{ testMeta.language ?? '—' }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Цена тарифа: </span>
                  <span
                    :class="{
                      'font-bold':
                        testMeta.specialOfferPrice == null && testMeta.agentOfferPrice == null,
                    }"
                  >
                    {{ testMeta.tariffPrice != null ? `${testMeta.tariffPrice} ₸` : '—' }}
                  </span>
                </p>
                <p>
                  <span class="font-medium text-gray-500">Цена агента: </span>
                  <span
                    :class="{
                      'font-bold':
                        testMeta.specialOfferPrice == null && testMeta.agentOfferPrice != null,
                    }"
                  >
                    {{ testMeta.agentOfferPrice != null ? `${testMeta.agentOfferPrice} ₸` : '—' }}
                  </span>
                </p>
                <p>
                  <span class="font-medium text-gray-500">Спец. предложение: </span>
                  <span :class="{ 'font-bold': testMeta.specialOfferPrice != null }">
                    {{
                      testMeta.specialOfferPrice != null ? `${testMeta.specialOfferPrice} ₸` : '—'
                    }}
                  </span>
                </p>
                <p class="sm:col-span-2">
                  <span class="font-medium text-gray-500">Цена в счёте: </span>
                  <span class="font-bold">{{
                    testMeta.billPrice != null ? `${testMeta.billPrice} ₸` : '—'
                  }}</span>
                </p>
                <p>
                  <span class="font-medium text-gray-500">Результаты открыты: </span
                  >{{ testMeta.isShared ? 'Да' : 'Нет' }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Порекомендовал другу: </span
                  >{{ testMeta.isRecommended ? 'Да' : 'Нет' }}
                </p>
              </div>
            </section>

            <section>
              <h3 class="text-lg font-semibold mb-3">Агент</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <p>
                  <span class="font-medium text-gray-500">Агент: </span
                  >{{ testMeta.agentName ?? '—' }}
                </p>
                <p>
                  <span class="font-medium text-gray-500">Вознаграждение (%): </span
                  >{{ testMeta.agentReward != null ? `${testMeta.agentReward}%` : '—' }}
                </p>
                <p class="sm:col-span-2">
                  <span class="font-medium text-gray-500">Реф. ссылка агента: </span
                  >{{ testMeta.recommendationId ?? '—' }}
                </p>
              </div>
            </section>
          </div>
        </template>
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<style scoped></style>
